import { Button, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Eye, EyeClosed, EyeClosedIcon, EyeIcon } from '@phosphor-icons/react';
import { useForm } from '@tanstack/react-form';
import { useState } from 'react';

import { ButtonLink } from '@/components/router/ButtonLink';
import type { SubmitPasswordResetDto } from '@/modules/api/backend';

interface PasswordResetFormProps {
  onSubmit: (values: SubmitPasswordResetDto) => Promise<void>;
}

export function PasswordResetForm({ onSubmit }: PasswordResetFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      const { confirmPassword, ...submitData } = value;
      await onSubmit(submitData);
    },
  });

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        await form.handleSubmit();
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h2">Neues Passwort festlegen</Typography>

        <form.Field
          name="password"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Passwort ist erforderlich';
              if (value.length < 8) return 'Passwort muss mindestens 8 Zeichen lang sein';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Neues Passwort"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Verstecken' : 'Anzeigen'}
                      >
                        {showPassword ? <EyeClosedIcon /> : <EyeIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </form.Field>

        <form.Field
          name="confirmPassword"
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!value) return 'Passwort-Bestätigung ist erforderlich';
              const password = fieldApi.form.getFieldValue('password');
              if (value !== password) return 'Passwörter stimmen nicht überein';

              return undefined;
            },
          }}
        >
          {(field) => (
            <TextField
              fullWidth
              error={field.state.meta.errors.length > 0}
              helperText={field.state.meta.errors[0]}
              label="Passwort bestätigen"
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              type={showPassword ? 'text' : 'password'}
              value={field.state.value}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Verstecken' : 'Anzeigen'}
                      >
                        {showPassword ? <EyeClosed /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </form.Field>

        <form.Subscribe selector={(formState) => [formState.canSubmit, formState.isSubmitting]}>
          {([canSubmit, isSubmitting]) => (
            <Stack alignItems="center" direction="row" gap={3} justifyContent="flex-end">
              <ButtonLink color="primary" to="/" variant="outlined">
                Zur Startseite
              </ButtonLink>
              <Button disabled={!canSubmit} type="submit" variant="contained">
                {isSubmitting ? <CircularProgress /> : 'Zurücksetzen'}
              </Button>
            </Stack>
          )}
        </form.Subscribe>
      </Stack>
    </form>
  );
}
